import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Weight, Ruler, Shield, Package, Gift, Palette, X, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback defaults (used if API fails)
const DEFAULT_BASE_COLORS = [
  { value: 'bleu', label: 'Bleu', label_en: 'Blue', hex: '#1E4C87' },
  { value: 'vert', label: 'Vert', label_en: 'Green', hex: '#0F4336' },
  { value: 'noir', label: 'Noir', label_en: 'Black', hex: '#0A0A0D' },
  { value: 'orange', label: 'Orange', label_en: 'Orange', hex: '#E75B12' },
];

const DEFAULT_RAL_COLORS = [
  { value: 'RAL 9010', label: 'Blanc Pur', hex: '#F7F9F7', ral: '9010' },
  { value: 'RAL 3000', label: 'Rouge Feu', hex: '#AB2524', ral: '3000' },
  { value: 'RAL 3020', label: 'Rouge Signalisation', hex: '#C1121C', ral: '3020' },
  { value: 'RAL 5002', label: 'Bleu Outremer', hex: '#2B2C7C', ral: '5002' },
  { value: 'RAL 5015', label: 'Bleu Ciel', hex: '#2874B2', ral: '5015' },
  { value: 'RAL 6018', label: 'Vert Jaune', hex: '#57A638', ral: '6018' },
  { value: 'RAL 6029', label: 'Vert Menthe', hex: '#20603D', ral: '6029' },
  { value: 'RAL 1003', label: 'Jaune de Sécurité', hex: '#F9A800', ral: '1003' },
  { value: 'RAL 1021', label: 'Jaune Colza', hex: '#EEC900', ral: '1021' },
  { value: 'RAL 2004', label: 'Orange Pur', hex: '#E75B12', ral: '2004' },
  { value: 'RAL 2008', label: 'Orange Clair', hex: '#F3752C', ral: '2008' },
  { value: 'RAL 4006', label: 'Pourpre', hex: '#A03472', ral: '4006' },
  { value: 'RAL 4010', label: 'Rose Telemagenta', hex: '#CF3476', ral: '4010' },
  { value: 'RAL 7016', label: 'Gris Anthracite', hex: '#383E42', ral: '7016' },
  { value: 'RAL 7035', label: 'Gris Clair', hex: '#CBD0CC', ral: '7035' },
  { value: 'RAL 7040', label: 'Gris Fenêtre', hex: '#9DA3A6', ral: '7040' },
  { value: 'RAL 8017', label: 'Brun Chocolat', hex: '#44322D', ral: '8017' },
  { value: 'RAL 8019', label: 'Brun Gris', hex: '#3D3635', ral: '8019' },
  { value: 'RAL 9001', label: 'Blanc Crème', hex: '#FDF4E3', ral: '9001' },
  { value: 'RAL 9016', label: 'Blanc Signalisation', hex: '#F7FBFF', ral: '9016' },
];

const DEFAULT_CONFIG_OPTIONS = {
  pneus: [
    { value: 'marathon', label: 'Schwalbe Marathon Plus 1.90', label_en: 'Schwalbe Marathon Plus 1.90', description: 'Pneu route anti-crevaison', description_en: 'Puncture-resistant road tire' },
    { value: 'gravel', label: 'CST Jet Gravel 1.90', label_en: 'CST Jet Gravel 1.90', description: 'Pneu polyvalent gravel', description_en: 'Versatile gravel tire' }
  ],
  transmissions: [
    { value: '1x10', label: '1x10 vitesses', label_en: '1x10 speed', description: "Configuration d'origine Deore", description_en: 'Standard Deore configuration' },
    { value: '1x12', label: '1x12 vitesses', label_en: '1x12 speed', description: 'Configuration premium Deore', description_en: 'Premium Deore configuration', price: 50 },
    { value: 'grx', label: 'Groupe Shimano GRX', label_en: 'Shimano GRX Groupset', description: 'Transmission gravel complète', description_en: 'Complete gravel drivetrain', price: 400 }
  ],
  guidons: [
    { value: 'vtt', label: 'Guidon VTT', label_en: 'MTB Handlebar', description: 'Position droite, confortable', description_en: 'Upright, comfortable position' },
    { value: 'gravel', label: 'Guidon Gravel', label_en: 'Gravel Handlebar', description: 'Position courbée, aérodynamique', description_en: 'Drop bar, aerodynamic position' }
  ],
  tailles: [
    { value: 'S', label: 'S', label_en: 'S', description: '155-170cm', description_en: '155-170cm' },
    { value: 'M', label: 'M', label_en: 'M', description: '170-180cm', description_en: '170-180cm' },
    { value: 'XL', label: 'XL', label_en: 'XL', description: '180-195cm', description_en: '180-195cm' }
  ],
  axes: [
    { value: 'QR', label: 'Axe QR 135/10', label_en: 'QR Axle 135/10', description: 'Quick Release standard', description_en: 'Standard Quick Release' },
    { value: 'traversant', label: 'Axe traversant 142/12', label_en: 'Thru-axle 142/12', description: 'Plus rigide et précis', description_en: 'More rigid and precise', price: 250 }
  ],
  axes_kit: [
    { value: 'QR', label: 'Axe QR 135/10', label_en: 'QR Axle 135/10', description: 'Quick Release standard', description_en: 'Standard Quick Release' },
    { value: 'traversant', label: 'Axe traversant 142/12', label_en: 'Thru-axle 142/12', description: 'Plus rigide et précis', description_en: 'More rigid and precise', price: 80 }
  ]
};

export function Configurator({ product, isKit = false }) {
  const { addToCart } = useCart();
  const { language } = useLanguage();
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  
  // Dynamic options from API
  const [configOptions, setConfigOptions] = useState(DEFAULT_CONFIG_OPTIONS);
  const [baseColors, setBaseColors] = useState(DEFAULT_BASE_COLORS);
  const [ralColors, setRalColors] = useState(DEFAULT_RAL_COLORS);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  
  const [config, setConfig] = useState({
    pneu: 'marathon',
    transmission: '1x10',
    guidon: 'vtt',
    couleur: 'bleu',
    couleurCustom: false,
    taille: 'M',
    axe: 'QR'
  });

  const [totalPrice, setTotalPrice] = useState(product.base_price);
  const [specsDialogOpen, setSpecsDialogOpen] = useState(false);

  // Fetch config options from API
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [optionsRes, colorsRes] = await Promise.all([
          axios.get(`${API}/config-options`),
          axios.get(`${API}/colors`)
        ]);
        
        if (optionsRes.data.options) {
          setConfigOptions(optionsRes.data.options);
        }
        if (colorsRes.data.base_colors) {
          setBaseColors(colorsRes.data.base_colors);
        }
        if (colorsRes.data.ral_colors) {
          setRalColors(colorsRes.data.ral_colors);
        }
        setOptionsLoaded(true);
      } catch (error) {
        // Error handled silently
        setOptionsLoaded(true);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    let price = product.base_price;
    
    // For bikes: check pneus, transmission and guidons prices
    if (!isKit) {
      const selectedPneu = configOptions.pneus?.find(opt => opt.value === config.pneu);
      if (selectedPneu?.price) price += selectedPneu.price;
      
      const selectedTransmission = configOptions.transmissions?.find(opt => opt.value === config.transmission);
      if (selectedTransmission?.price) price += selectedTransmission.price;
      
      const selectedGuidon = configOptions.guidons?.find(opt => opt.value === config.guidon);
      if (selectedGuidon?.price) price += selectedGuidon.price;
    }
    
    // For both bikes and kits: check taille price
    const selectedTaille = configOptions.tailles?.find(opt => opt.value === config.taille);
    if (selectedTaille?.price) price += selectedTaille.price;
    
    // Axe price (different for kit vs bike)
    const axeOptions = isKit ? (configOptions.axes_kit || configOptions.axes) : configOptions.axes;
    const selectedAxe = axeOptions?.find(opt => opt.value === config.axe);
    if (selectedAxe?.price) price += selectedAxe.price;
    
    // Custom color adds 50€
    if (config.couleurCustom) {
      price += 50;
    }
    setTotalPrice(price);
  }, [config, product.base_price, configOptions, isKit]);

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleBaseColorSelect = (color) => {
    setConfig(prev => ({ 
      ...prev, 
      couleur: color.value, 
      couleurCustom: false 
    }));
  };

  const handleCustomColorSelect = (color) => {
    setConfig(prev => ({ 
      ...prev, 
      couleur: color.value, 
      couleurCustom: true 
    }));
    setColorDialogOpen(false);
    toast.success(language === 'fr' 
      ? `Couleur ${color.label} sélectionnée (+50€)` 
      : `Color ${color.label_en || color.label} selected (+€50)`);
  };

  const getSelectedColorInfo = () => {
    const baseColor = baseColors.find(c => c.value === config.couleur);
    if (baseColor) return { ...baseColor, isCustom: false };
    
    const customColor = ralColors.find(c => c.value === config.couleur);
    if (customColor) return { ...customColor, isCustom: true };
    
    return baseColors[0] || DEFAULT_BASE_COLORS[0];
  };

  const handleAddToCart = () => {
    const configToSave = isKit 
      ? {
          couleur: config.couleur, 
          couleurCustom: config.couleurCustom, 
          taille: config.taille, 
          axe: config.axe
        }
      : {
          pneu: config.pneu,
          transmission: config.transmission,
          guidon: config.guidon,
          couleur: config.couleur, 
          couleurCustom: config.couleurCustom, 
          taille: config.taille, 
          axe: config.axe
        };

    // Calculate final price with promo if active
    const finalPrice = product.promo_active && product.promo_price 
      ? totalPrice - product.base_price + product.promo_price 
      : totalPrice;

    addToCart({
      product_id: product.id,
      product_name: product.name,
      product_type: product.type,
      config: configToSave,
      quantity: 1,
      unit_price: finalPrice,
      total_price: finalPrice
    });
    toast.success(language === 'fr' ? 'Produit ajouté au panier' : 'Product added to cart');
  };

  const ConfigOption = ({ options, selected, onChange, type = 'default' }) => (
    <div className={`grid gap-3 ${type === 'color' ? 'grid-cols-4 sm:grid-cols-6 md:grid-cols-8' : 'grid-cols-2 md:grid-cols-3'}`}>
      {options.map((option) => (
        <motion.button
          key={option.value}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onChange(option.value)}
          className={`relative border p-3 transition-all duration-200 text-left ${
            selected === option.value 
              ? 'border-black border-2 bg-secondary' 
              : 'border-border hover:border-black'
          }`}
          data-testid={`config-${option.value}`}
        >
          {type === 'color' ? (
            <div className="flex flex-col items-center gap-2">
              <div 
                className={`w-8 h-8 rounded-full border-2 ${selected === option.value ? 'border-black' : 'border-border'}`}
                style={{ backgroundColor: option.hex }}
              />
              <span className="font-mono text-[10px] uppercase text-center leading-tight">
                {language === 'en' && option.label_en ? option.label_en : option.label}
              </span>
              {option.ral && (
                <span className="font-mono text-[8px] text-muted-foreground">{option.ral}</span>
              )}
            </div>
          ) : (
            <>
              <span className="font-heading font-bold text-sm uppercase block">
                {language === 'en' && option.label_en ? option.label_en : option.label}
                {option.price && option.price > 0 && (
                  <span className="text-[#0047AB] ml-1">(+{option.price}€)</span>
                )}
              </span>
              {option.description && (
                <span className="font-mono text-xs text-muted-foreground mt-1 block">
                  {language === 'en' && option.description_en ? option.description_en : option.description}
                </span>
              )}
            </>
          )}
          {selected === option.value && (
            <div className="absolute top-2 right-2">
              <Check className="w-4 h-4" />
            </div>
          )}
        </motion.button>
      ))}
    </div>
  );

  // State for image gallery
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const productImages = product.images && product.images.length > 0 
    ? product.images 
    : [product.image];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
      {/* Product Image Gallery */}
      <div className="lg:sticky lg:top-24 lg:h-fit">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative"
        >
          {/* Main Image */}
          <div className="aspect-square bg-secondary overflow-hidden relative">
            <img 
              src={productImages[currentImageIndex]} 
              alt={product.name}
              className="w-full h-full object-contain p-8"
              data-testid="product-image"
            />
            
            {/* Navigation arrows - only show if multiple images */}
            {productImages.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white border border-border flex items-center justify-center transition-colors"
                  data-testid="prev-image"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white border border-border flex items-center justify-center transition-colors"
                  data-testid="next-image"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                
                {/* Image counter */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 font-mono text-xs">
                  {currentImageIndex + 1} / {productImages.length}
                </div>
              </>
            )}
          </div>

          {/* Thumbnails - only show if multiple images */}
          {productImages.length > 1 && (
            <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
              {productImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`flex-shrink-0 w-16 h-16 border-2 overflow-hidden transition-all ${
                    idx === currentImageIndex ? 'border-black' : 'border-border hover:border-gray-400'
                  }`}
                  data-testid={`thumbnail-${idx}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Configuration */}
      <div className="space-y-8">
        <div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight"
          >
            {product.name}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-body text-muted-foreground mt-2"
          >
            {product.description}
          </motion.p>
        </div>

        {/* Price */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="border-b border-border pb-6"
        >
          <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            {language === 'fr' ? 'Prix' : 'Price'}
          </span>
          
          {/* Promo banner */}
          {product.promo_active && product.promo_price && product.promo_end_date && (
            <div className="bg-orange-500 text-white px-3 py-1 mt-2 mb-2 inline-block">
              <span className="font-mono text-xs uppercase font-bold">
                🏷️ {language === 'fr' ? 'En promo jusqu\'au' : 'On sale until'} {new Date(product.promo_end_date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
              </span>
            </div>
          )}
          
          <div className="price-ticker mt-1" data-testid="product-price">
            {product.promo_active && product.promo_price ? (
              <div className="flex items-baseline gap-3">
                <span className="text-[#0047AB]">{formatPrice(totalPrice - product.base_price + product.promo_price)}</span>
                <span className="text-muted-foreground line-through text-2xl">{formatPrice(totalPrice)}</span>
              </div>
            ) : (
              formatPrice(totalPrice)
            )}
          </div>
        </motion.div>

        {/* Config Sections */}
        <div className="space-y-14">
          {/* Pneus - Only for bikes, not kits */}
          {!isKit && (
            <div>
              <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
                {language === 'fr' ? 'Pneus' : 'Tires'}
              </h3>
              <ConfigOption 
                options={configOptions.pneus || []}
                selected={config.pneu}
                onChange={(v) => handleConfigChange('pneu', v)}
              />
            </div>
          )}

          {/* Transmission - Only for bikes, not kits */}
          {!isKit && (
            <div>
              <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
                {language === 'fr' ? 'Transmission' : 'Drivetrain'}
              </h3>
              <ConfigOption 
                options={configOptions.transmissions || []}
                selected={config.transmission}
                onChange={(v) => handleConfigChange('transmission', v)}
              />
            </div>
          )}

          {/* Guidon - Only for bikes, not kits */}
          {!isKit && (
            <div>
              <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
                {language === 'fr' ? 'Guidon' : 'Handlebar'}
              </h3>
              <ConfigOption 
                options={configOptions.guidons || []}
                selected={config.guidon}
                onChange={(v) => handleConfigChange('guidon', v)}
              />
            </div>
          )}

          {/* Couleur */}
          <div>
            <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
              {language === 'fr' ? 'Couleur' : 'Color'}
            </h3>
            
            {/* Couleurs de base */}
            <div className="grid grid-cols-4 gap-3 mb-4">
              {baseColors.map((color) => (
                <motion.button
                  key={color.value}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleBaseColorSelect(color)}
                  className={`relative border p-4 transition-all duration-200 ${
                    config.couleur === color.value && !config.couleurCustom
                      ? 'border-black border-2 bg-secondary' 
                      : 'border-border hover:border-black'
                  }`}
                  data-testid={`color-${color.value}`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div 
                      className={`w-10 h-10 rounded-full border-2 ${
                        config.couleur === color.value && !config.couleurCustom ? 'border-black' : 'border-border'
                      }`}
                      style={{ backgroundColor: color.hex }}
                    />
                    <span className="font-mono text-xs uppercase text-center">
                      {language === 'en' && color.label_en ? color.label_en : color.label}
                    </span>
                  </div>
                  {config.couleur === color.value && !config.couleurCustom && (
                    <div className="absolute top-2 right-2">
                      <Check className="w-4 h-4" />
                    </div>
                  )}
                </motion.button>
              ))}
            </div>

            {/* Bouton couleur personnalisée */}
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => setColorDialogOpen(true)}
              className={`w-full border p-4 transition-all duration-200 ${
                config.couleurCustom
                  ? 'border-black border-2 bg-secondary' 
                  : 'border-border hover:border-black'
              }`}
              data-testid="custom-color-btn"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full border-2 border-border bg-gradient-to-br from-red-500 via-blue-500 to-green-500 flex items-center justify-center">
                    <Palette className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <span className="font-heading font-bold text-sm uppercase block">
                      {language === 'fr' ? 'Couleur personnalisée' : 'Custom color'}
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {language === 'fr' ? 'Nuancier RAL (+50€)' : 'RAL color chart (+€50)'}
                    </span>
                  </div>
                </div>
                {config.couleurCustom && (
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-8 h-8 rounded-full border-2 border-black"
                      style={{ backgroundColor: getSelectedColorInfo().hex }}
                    />
                    <span className="font-mono text-xs">{getSelectedColorInfo().label}</span>
                    <Check className="w-4 h-4" />
                  </div>
                )}
              </div>
            </motion.button>

            {/* Dialog nuancier RAL */}
            <Dialog open={colorDialogOpen} onOpenChange={setColorDialogOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold uppercase flex items-center gap-2">
                    <Palette className="w-5 h-5" />
                    {language === 'fr' ? 'Nuancier RAL (+50€)' : 'RAL Color Chart (+€50)'}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    {language === 'fr' ? 'Sélectionnez une couleur personnalisée RAL' : 'Select a custom RAL color'}
                  </DialogDescription>
                </DialogHeader>
                
                {/* Warning */}
                <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 mt-4">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-heading font-bold text-sm text-yellow-800">
                      {language === 'fr' ? 'Disponibilité sur demande' : 'Availability on request'}
                    </p>
                    <p className="font-body text-xs text-yellow-700 mt-1">
                      {language === 'fr' 
                        ? 'Les couleurs personnalisées sont soumises à la disponibilité du peintre. Nous vous contacterons pour confirmer le délai.'
                        : 'Custom colors are subject to painter availability. We will contact you to confirm the delivery time.'}
                    </p>
                  </div>
                </div>

                {/* Color Grid */}
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-3 mt-6">
                  {ralColors.map((color) => (
                    <motion.button
                      key={color.value}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleCustomColorSelect(color)}
                      className="border border-border p-3 hover:border-black transition-all flex flex-col items-center gap-2"
                      data-testid={`ral-${color.ral}`}
                    >
                      <div 
                        className="w-10 h-10 rounded-full border border-border"
                        style={{ backgroundColor: color.hex }}
                      />
                      <span className="font-mono text-[10px] uppercase text-center leading-tight">
                        {color.label}
                      </span>
                      <span className="font-mono text-[9px] text-muted-foreground">
                        RAL {color.ral}
                      </span>
                    </motion.button>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Taille */}
          <div>
            <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
              {language === 'fr' ? 'Taille' : 'Size'}
            </h3>
            <ConfigOption 
              options={configOptions.tailles || []}
              selected={config.taille}
              onChange={(v) => handleConfigChange('taille', v)}
            />
          </div>

          {/* Axe (with different pricing for bikes vs kits) */}
          <div>
            <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
              {language === 'fr' ? "Type d'axe" : 'Axle type'}
            </h3>
            <ConfigOption 
              options={isKit ? (configOptions.axes_kit || configOptions.axes || []) : (configOptions.axes || [])}
              selected={config.axe}
              onChange={(v) => handleConfigChange('axe', v)}
            />
          </div>
        </div>

        {/* Add to Cart */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="pt-8 border-t border-border"
        >
          <Button 
            onClick={handleAddToCart}
            className="w-full h-14 rounded-none font-mono uppercase tracking-widest text-sm"
            data-testid="add-to-cart-button"
          >
            {language === 'fr' ? 'Ajouter au panier' : 'Add to cart'} — {formatPrice(product.promo_active && product.promo_price ? totalPrice - product.base_price + product.promo_price : totalPrice)}
          </Button>
        </motion.div>

        {/* Technical Specifications Button */}
        {product.specs && Object.keys(product.specs).length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-6"
          >
            <Button 
              onClick={() => setSpecsDialogOpen(true)}
              variant="outline"
              className="w-full h-12 rounded-none font-mono uppercase tracking-widest text-xs border-2 border-black hover:bg-black hover:text-white transition-all"
              data-testid="specs-button"
            >
              <Package className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Caractéristiques techniques' : 'Technical Specifications'}
            </Button>
          </motion.div>
        )}

        {/* Technical Specifications Dialog */}
        <Dialog open={specsDialogOpen} onOpenChange={setSpecsDialogOpen}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-xl uppercase">
                {language === 'fr' ? 'Caractéristiques techniques' : 'Technical Specifications'}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {language === 'fr' ? 'Détails techniques du produit' : 'Product technical details'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <ul className="space-y-3">
                {product.specs && Object.entries(product.specs).map(([key, spec]) => spec && spec.label && (
                  <li key={key} className="flex justify-between items-baseline border-b border-border/50 pb-2">
                    <span className="font-mono text-sm text-muted-foreground">
                      {language === 'en' && spec.label_en ? spec.label_en : spec.label}
                    </span>
                    <span className="font-mono text-sm font-medium text-right ml-4">
                      {spec.value}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Delivery info */}
            <div className="p-6 bg-black text-white">
              <p className="font-heading font-bold uppercase">
                {language === 'fr' ? 'Livré monté complet' : 'Delivered fully assembled'}
              </p>
              <p className="font-body text-sm text-gray-400 mt-2">
                {language === 'fr' 
                  ? "Votre vélo arrive prêt à rouler"
                  : "Your bike arrives ready to ride"}
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
